<template>
  <div class="grid">
    <template v-if="loading">
      <mds-loader />
    </template>
    <template v-else>
      <template v-if="rows.length > 0">
        <div class="pending-lineups__applyall-title-container">
          <div class="pending-lineups__check">
            <PendingCheckBox
              @change="selectAll"
              :checked="applyAll.select"
              :indeterminate="applyAll.isIndeterminateSelected"
            />
          </div>
          <p class="pending-lineups__applyall-title">
            Apply All
          </p>
        </div>
        <template v-for="(client, clientRow) in rows">
          <div class="pending-lineups__client-wrapper" :key="client.id">
            <div class="pending-lineups__client-title-container">
              <div class="pending-lineups__check">
                <PendingCheckBox
                  :checked="client.select"
                  :indeterminate="client.isIndeterminateSelected"
                  @change="selectAll(...arguments, client.id)"
                />
              </div>
              <h1 class="pending-lineups__client-title">
                <CollapseButton
                  :title="'Open/Close Client Products'"
                  :collapsed="client.__hidden"
                  @toggle="toggleClientProducts(clientRow)"
                >
                  <span>{{ client.name }}</span>
                </CollapseButton>
              </h1>
            </div>
            <template v-for="(product, productRow) in client.products">
              <div :key="`${client.id}-${product.id}`" v-show="!product.__hidden">
                <div class="pending-lineups__product-title-container">
                  <div class="pending-lineups__check">
                    <PendingCheckBox
                      :checked="product.select"
                      :indeterminate="product.isIndeterminateSelected"
                      @change="selectAll(...arguments, client.id, product.id)"
                    />
                  </div>
                  <h2 class="pending-lineups__product-title">
                    <CollapseButton
                      :title="'Open/Close Product Lineups'"
                      :collapsed="product.lineupsSection.__hidden"
                      @toggle="toggleProductLineups(clientRow, productRow)"
                    >
                      <span>{{ product.name }} ({{ product.code }})</span>
                    </CollapseButton>
                  </h2>
                </div>
                <div
                  class="pending-lineups__lineups-container"
                  v-show="!product.lineupsSection.__hidden"
                >
                  <DataTable :data="product.lineups" :columnsConfig="columnsConfig">
                    <template v-slot:cell="{ content, row, col, sub }">
                      <div
                        v-if="columnsConfig[col].key === 'select'"
                        class="pending-lineups__check"
                      >
                        <template v-if="localPolicyOptions.options['submit-to-production']">
                          <template v-if="sub === undefined">
                            <PendingCheckBox
                              @change="
                                selectAll(
                                  ...arguments,
                                  client.id,
                                  product.id,
                                  product.lineups[row].id
                                )
                              "
                              :checked="content"
                            />
                          </template>
                        </template>
                      </div>
                      <template v-else-if="columnsConfig[col].key === 'name' && sub === undefined">
                        <div class="'pending-lineups_lineup'">
                          <CollapseButton
                            :title="'Open/Close Lineup Funds'"
                            :collapsed="product.lineups[row].__hidden"
                            @toggle="toggleFunds(clientRow, productRow, row)"
                          >
                            <span>{{ content }}</span>
                          </CollapseButton>
                        </div>
                      </template>
                      <template v-else-if="columnsConfig[col].key === 'name'">
                        <div class="pending-lineups_fund">
                          {{ content }}
                        </div>
                      </template>
                      <div
                        class="pending-lineups__undo"
                        v-else-if="columnsConfig[col].key === 'undo'"
                      >
                        <template v-if="sub === undefined">
                          <div class="action-container">
                            <mds-button variation="icon-only" icon="undo" type="button" class="baseGridIcon"
                              @mouseover="$emit('showToolTip', $event, ['Undo Lineup Changes'],undefined, 'small')"
                              @mouseleave="$emit('hideToolTip')"
                              @click.prevent="undo(product.lineups[row].action)"
                            >
                            </mds-button>
                          </div>
                        </template>
                      </div>
                      <template v-else>
                        {{ content }}
                      </template>
                    </template>
                  </DataTable>
                </div>
              </div>
            </template>
          </div>
        </template>
      </template>
      <template v-else>
        <p class="title-message">Changes to Lineups have not been added</p>
      </template>
    </template>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import {
  cloneDeep,
  get,
  orderBy,
  sortBy,
} from 'lodash';
import { format } from 'date-fns';

import PendingCheckBox from '@/components/atoms/PendingCheckBox';
import CollapseButton from '@/components/atoms/CollapseButton';
import DataTable from '@/components/molecules/data-table/table';
import { getFiduciaryPendingLinesups } from '@/services/Fiduciaries';

const columnsConfig = require('@/config/pending-lineups-columns.json');

export default {
  components: {
    PendingCheckBox,
    CollapseButton,
    DataTable,
  },
  data() {
    return {
      data: [],
      rows: [],
      columnsConfig,
      loading: false,
      request: null,
      applyAll: {
        select: false,
        isIndeterminateSelected: false,
      },
    };
  },
  computed: {
    ...mapState({
      policies: (state) => state.user.policies,
      selectedFiduciary: (state) => state.user.fiduciary.id,
      selectedClient: (state) => state.pending.client.selected,
      selectedClientName: (state) => state.pending.client.name,
      searchTerm: (state) => state.pending.searchTerm,
      selectedTab: (state) => state.pending.tab,
      needsReloading: (state) => state.pending.needsReloading,
    }),
    currentViewName() {
      return this.selectedClient > 0 ? 'one-client' : 'all-clients';
    },
    localPolicyOptions() {
      return get(this.policies, `pending.views.${this.currentViewName}.tabs.${this.selectedTab}`);
    },
  },
  watch: {
    selectedClient() {
      this.$store.dispatch('setPendingItemsSelected', { list: [], total: 0 });
      this.applyAll.isTotalSelected = false;
      this.applyAll.isIndeterminateSelected = false;
      this.filterData();
    },
    searchTerm() {
      this.$store.dispatch('setPendingItemsSelected', { list: [], total: 0 });
      this.applyAll.isTotalSelected = false;
      this.applyAll.isIndeterminateSelected = false;
      this.filterData();
    },
    needsReloading(newValue) {
      if (newValue === true) {
        this.$store.dispatch('setPendingItemsSelected', { list: [], total: 0 });
        this.applyAll.isTotalSelected = false;
        this.applyAll.isIndeterminateSelected = false;
        this.loadData();
      }
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    loadData() {
      if (this.loading) {
        this.$request.abort(this.request);
      }
      this.loading = true;
      this.data = [];

      getFiduciaryPendingLinesups({
        selectedFiduciary: this.selectedFiduciary,
      }).then((result) => {
        this.loading = false;
        const { status = {}, data = [] } = result;
        if (status.success) {
          const { products = [] } = data;
          this.data = this.formatData(products);
          this.filterData();
        }
      }).catch((error) => {
        const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;
        if (httpStatus === 401) {
          this.$router.push('/Login');
        }
      }).finally(() => {
        this.loading = false;
        this.$store.dispatch('setReloadNeed', false);
      });
    },
    selectAll(
      checkedValue,
      value,
      clientId = undefined,
      productId = undefined,
      lineupId = undefined,
    ) {
      /* eslint-disable no-param-reassign */
      const selectAllClients = clientId === undefined;
      const selectedRows = [];
      let clientSelectedCount = 0;
      this.rows = this.rows.map((client) => {
        if (selectAllClients || client.id === clientId) {
          client.select = checkedValue;
        }
        let productSelectedCount = 0;
        const selectAllProducts = selectAllClients || (clientId === client.id && productId === undefined);
        client.products = client.products.map((product) => {
          if (selectAllProducts || product.id === productId) {
            product.select = checkedValue;
          }
          let lineupSelectedCount = 0;
          const selectAllLineups = selectAllProducts || (productId === product.id && lineupId === undefined);
          product.lineups = product.lineups.map((lineup) => {
            if (selectAllLineups || lineup.id === lineupId) {
              lineup.select = checkedValue;
            }
            if (lineup.select) {
              lineupSelectedCount += 1;
              selectedRows.push(lineup.action);
            }
            return lineup;
          });
          Object.assign(
            product,
            this.calculateIndeterminate(lineupSelectedCount, product.lineups.length),
          );
          if (product.select) {
            productSelectedCount += 1;
          }
          return product;
        });
        Object.assign(
          client,
          this.calculateIndeterminate(productSelectedCount, client.products.length),
        );
        if (client.select) {
          clientSelectedCount += 1;
        }
        return client;
      });
      Object.assign(
        this.applyAll,
        this.calculateIndeterminate(clientSelectedCount, this.rows.length),
      );

      this.$store.dispatch('setPendingItemsSelected', {
        total: selectedRows.length,
        list: selectedRows.map((item) => item),
      });
      /* eslint-enable no-param-reassign */
    },
    undo(payload) {
      this.$store.dispatch('openModal', {
        title: 'Undo Lineup Changes',
        component: 'lineup-undo-changes',
        data: {
          funds: payload,
        },
      });
    },
    calculateIndeterminate(totalSelected, totalRows) {
      let select;
      let isIndeterminateSelected;
      if (totalSelected === 0) {
        select = false;
        isIndeterminateSelected = false;
      } else if (totalSelected === totalRows) {
        select = true;
        isIndeterminateSelected = false;
      } else {
        select = false;
        isIndeterminateSelected = totalSelected > 0 && totalSelected < totalRows;
      }
      return {
        select,
        isIndeterminateSelected,
      };
    },
    filterData() {
      const rows = cloneDeep(this.data);
      const filteredByClient = this.filterByClient(rows);
      const filteredKeyWord = this.filterByKeyword(filteredByClient);
      this.rows = filteredKeyWord;
    },
    filterByClient(rows) {
      if (this.selectedClient !== 0) {
        return rows.filter((client) => client.id === this.selectedClient);
      }
      return rows;
    },
    filterByKeyword(rows) {
      /* eslint-disable no-param-reassign */
      if (this.searchTerm) {
        return rows.reduce((acClient, client) => {
          client.products = client.products.reduce((acProduct, product) => {
            product.lineups = product.lineups.reduce((acLineups, lineup) => {
              lineup.subs = lineup.subs.filter((fund) => {
                const matchName = new RegExp(this.searchTerm, 'gi').test(fund.name);
                const matchTicker = new RegExp(this.searchTerm, 'gi').test(fund.ticker);
                const matchsecID = new RegExp(this.searchTerm, 'gi').test(fund.secID);
                return matchName || matchTicker || matchsecID;
              });
              if (lineup.subs.length > 0) {
                acLineups.push(lineup);
              }
              return acLineups;
            }, []);
            if (product.lineups.length > 0) {
              acProduct.push(product);
            }
            return acProduct;
          }, []);
          if (client.products.length > 0) {
            acClient.push(client);
          }
          return acClient;
        }, []);
      }
      /* eslint-enable no-param-reassign */
      return rows;
    },
    formatData(data) {
      /* eslint-disable no-param-reassign */
      let response = data.reduce((fiduciaryClients, item) => {
        const {
          fiduciaryClient: { id: fiduciaryClientId },
        } = item;
        let fiduciaryClient = fiduciaryClients.find((el) => el.id === fiduciaryClientId);

        if (!fiduciaryClient) {
          const newClient = {
            id: get(item, 'fiduciaryClient.id', 0),
            name: get(item, 'fiduciaryClient.client.clientName', ''),
            select: false,
            isIndeterminateSelected: false,
            __hidden: false,
            products: [],
          };
          fiduciaryClient = newClient;
          fiduciaryClients.push(fiduciaryClient);
        }
        const newProduct = {
          id: get(item, 'productId', 0),
          name: get(item, 'productName', 0),
          code: get(item, 'productCode', 0),
          select: false,
          isIndeterminateSelected: false,
          __hidden: false,
          lineupsSection: {
            __hidden: false,
          },
          lineups: [],
        };

        newProduct.lineups = item.menus.map((menu) => {
          const programName = get(item, 'program.programName', '');
          const menuName = get(menu, 'menuName', '');
          const newLineup = {
            id: get(menu, 'menuId', 0),
            name: `${programName} ${menuName}`,
            select: false,
            action: [],
            __hidden: false,
            subs: [],
            /*
             * These are the Lineup Funds is called subs because in
             * the Data Table component subs are the sub level data
             */
          };

          let lastUpdatedFundTime = 0;
          newLineup.subs = menu.funds.map((fund) => {
            const time = new Date(fund.dateOfChange);
            const newFund = {
              name: get(fund, 'displayName', ''),
              ticker: get(fund, 'ticker', ''),
              secID: get(fund, 'secID', ''),
              updates: get(fund, 'action', ''),
              categoryName: get(fund, 'psaCategory[0].category.name', ''),
              time,
              __hidden: false,
            };
            lastUpdatedFundTime = Math.max(lastUpdatedFundTime, time);
            newLineup.action.push({
              fiduciaryFundId: fund.id,
              menuId: newLineup.id,
            });
            return newFund;
          });
          newLineup.subs = orderBy(newLineup.subs, (o) => o.time, 'desc');
          newLineup.time = lastUpdatedFundTime;
          newLineup.date = format(lastUpdatedFundTime, 'MMM D, YYYY');

          return newLineup;
        });

        newProduct.lineups = orderBy(newProduct.lineups, (o) => o.time, 'desc');
        fiduciaryClient.products.push(newProduct);

        return fiduciaryClients;
      }, []);

      response = sortBy(response, (client) => client.name.toLowerCase()).map((client) => {
        client.products = sortBy(client.products, (product) => product.name.toLowerCase());
        return client;
      });

      /* eslint-enable no-param-reassign */
      return response;
    },
    showToolTip(event, message) {
      this.$emit('showToolTip', event, message, undefined, 'small');
    },
    hideToolTip() {
      this.$emit('hideToolTip');
    },
    toggleClientProducts(row) {
      /* eslint-disable no-param-reassign */
      const client = this.rows[row];
      const nextValue = !client.__hidden;
      client.__hidden = nextValue;
      client.products = client.products
        && client.products.map((product) => {
          product.__hidden = nextValue;
          return product;
        });
      /* eslint-enable no-param-reassign */
    },
    toggleProductLineups(clientRow, row) {
      const product = this.rows[clientRow].products[row];
      const nextValue = !product.lineupsSection.__hidden;
      product.lineupsSection.__hidden = nextValue;
    },
    toggleFunds(clientRow, productRow, row) {
      /* eslint-disable no-param-reassign */
      const lineup = this.rows[clientRow].products[productRow].lineups[row];
      const nextValue = !lineup.__hidden;
      lineup.__hidden = nextValue;
      lineup.subs = lineup.subs
        && lineup.subs.map((fund) => {
          fund.__hidden = nextValue;
          return fund;
        });
      /* eslint-enable no-param-reassign */
    },
  },
};
</script>
<style lang="scss">
.pending-lineups {
  .grid {
    height: 1px; // Flex fix
    padding: 0 $mds-space-2-x;
  }
  .mds-data-table {
    width: 100%;
  }
  .mds-data-table__header-cell {
    vertical-align: top;
  }
  .mds-data-table__header-cell[aria-sort] {
    cursor: pointer;
  }
  .mds-data-table__header-cell-inner {
    padding-top: $mds-space-1-x;
  }
  .mds-data-table__header-cell-inner[role="button"]:focus {
    box-shadow: none;
  }
  .mds-data-table__cell {
    height: 40px;
    vertical-align: middle;
  }
}

.pending-lineups {
  // Specific Pending table styles
  &__applyall-title-container,
  &__client-title-container,
  &__product-title-container {
    display: table;
    width: 100%;

    .pending-lineups__check {
      display: table-cell;
      padding-left: $mds-space-1-x;
      vertical-align: middle;
      width: 32px;
    }
  }
  &__applyall-title-container {
    background-color: $mds-color-neutral-95;
    padding: 0 $mds-space-2-x;
  }
  &__client-wrapper {
    border-bottom: 1px solid $mds-color-neutral-80;
    margin: $mds-space-3-x 0;
    padding-left: $mds-space-2-x;
  }
  &__applyall-title {
    @include mds-body-text-m();
  }
  &__client-title {
    @include mds-body-text-l();
    margin: 0;
  }
  &__product-title-container {
    margin-left: $mds-space-4-x;
  }
  &__product-title {
    @include mds-body-text-l();
  }
  &__lineups-container {
    margin-left: $mds-space-8-x;
    margin-bottom: $mds-space-2-x;

    .pending-lineups__check {
      display: block;
      padding-left: $mds-space-1-x;
      width: auto;
    }
  }
  &_lineup {
    font-weight: $mds-font-weight-regular;
  }
  &_fund {
    padding-left: $mds-space-2-x;
  }
  .level-0:hover {
    box-shadow: 0 -2px 4px 0 rgba(70, 60, 60, 0.08), 0 2px 4px 0 rgba(0, 0, 0, 0.08);
    .mds-data-table__cell {
      background: #f2fbff;
    }
  }
  .level-1:hover {
    box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.08), 0 2px 4px 0 rgba(0, 0, 0, 0.08);
    .mds-data-table__cell {
      background: #f7fffd;
    }
  }

  .level-0:hover &__undo .baseGridIcon {
    background-color: #e0f3fb;

    .tg-cell-hover-icon {
      fill: $mds-color-neutral-37;
    }
  }
  .level-1:hover &__undo .baseGridIcon {
    background-color: #e1f6f0;

    .tg-cell-hover-icon {
      fill: $mds-color-neutral-37;
    }
  }
}

// Undo icon
.pending-lineups {
  .mds-data-table__row &__undo .action-container {
    width: $mds-space-inset-4-x;
    height: $mds-space-inset-4-x;
  }
  .mds-data-table__row &__undo .baseGridIcon {
    width: $mds-space-inset-4-x;
    height: $mds-space-inset-4-x;
    border-radius: 6px;
    display: none;
  }
  .level-0:hover &__undo .baseGridIcon {
    display: block;
  }

  .level-1:hover &__undo .baseGridIcon {
    display: block;
  }
}

.title-message {
  @include mds-level-1-heading();
  font-weight: $mds-font-weight-thin;
  color: $mds-text-color-primary-on-light;
  margin: $mds-space-8-x auto;
  text-align: center;
  width: 50%;
}
</style>
